"use client";

import { useEffect, useRef, useState } from "react";
import { ExternalLink, Sparkles, Square, Volume2 } from "lucide-react";
import type { Conversation } from "@/hooks/use-webrtc";

export type ChatMessagesProps = {
  conversation: Conversation[];
  agentName: string;
  avatarUrl: string;
  /**
   * 会話の先頭に置く挨拶。回答と同じ吹き出しで出す。
   * 会話が進んでも最初の発言として残る
   */
  greeting?: string;
  /**
   * 挨拶も会話も無いときに中央へ置く操作の案内。
   * 挨拶がある場合は出さない（画面を説明で埋めない）
   */
  emptyMessage?: string;
  /**
   * 回答の吹き出しに「もう一度聞く」を出すか。
   * 埋め込みウィジェットは訪問者向けの導線を増やさないため出さない
   */
  showReplay?: boolean;
};

export default function ChatMessages({
  conversation,
  agentName,
  avatarUrl,
  greeting,
  emptyMessage,
  showReplay = false,
}: ChatMessagesProps) {
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [conversation]);

  // 挨拶は回答と同じ行として先頭に積む。吹き出しの描き方を二重に持たずに済む
  const messages: Conversation[] = greeting?.trim()
    ? [{ role: "assistant", text: greeting.trim(), timestamp: "", isFinal: true }, ...conversation]
    : conversation;

  // 操作の案内は、挨拶も会話も無いときの穴埋め。挨拶があるならそちらに任せる
  if (messages.length === 0 && emptyMessage) {
    return (
      <div className="flex flex-1 items-center justify-center px-10">
        <p className="whitespace-pre-line text-center text-sm leading-relaxed text-slate-400">
          {emptyMessage}
        </p>
      </div>
    );
  }

  return (
    <div ref={scrollRef} className="showcan-messages flex-1 overflow-y-auto p-4 space-y-4">
      {messages.map((msg, i) => {
        if (msg.role === "assistant") {
          return (
            <div key={i} className="showcan-message showcan-message--assistant flex items-start gap-2">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[var(--showcan-ink-20)] flex items-center justify-center">
                <Sparkles className="w-4 h-4 text-[var(--showcan-ink)]" />
              </div>
              <div className="flex flex-col max-w-[75%]">
                <span className="text-[11px] text-slate-400 font-medium mb-1">
                  {agentName}
                </span>
                <div className="showcan-bubble showcan-bubble--assistant whitespace-pre-line break-words bg-[var(--showcan-ink-10)] rounded-2xl rounded-bl-none px-4 py-2 text-sm text-slate-700">
                  {msg.text}
                </div>
                {msg.products && msg.products.length > 0 && (
                  <div className="showcan-products mt-2 flex flex-wrap gap-2">
                    {msg.products.map((product) => (
                      <a
                        key={product.id}
                        href={product.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="showcan-product-link inline-flex max-w-full items-center gap-1 break-all rounded-full border border-[var(--showcan-ink-40)] bg-white px-3 py-1 text-xs text-[var(--showcan-ink-strong)] transition-colors hover:bg-[var(--showcan-ink-10)]"
                      >
                        {product.name}
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    ))}
                  </div>
                )}
                {showReplay && msg.audioUrl && <ReplayButton url={msg.audioUrl} />}
              </div>
            </div>
          );
        }

        return (
          <div key={i} className="showcan-message showcan-message--user flex justify-end">
            <div className="flex flex-col items-end max-w-[75%]">
              <div className="showcan-bubble showcan-bubble--user break-words bg-[var(--showcan-ink)] text-white rounded-2xl rounded-br-none px-4 py-2 text-sm">
                {msg.text ? (
                  msg.text
                ) : (
                  // 発話は検知したが、まだ聞き取り結果が届いていない状態
                  <span className="flex items-center gap-1 py-1" aria-label="聞き取り中">
                    <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-white/80 [animation-delay:-0.3s]" />
                    <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-white/80 [animation-delay:-0.15s]" />
                    <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-white/80" />
                  </span>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/**
 * 読み上げを聞き直すボタン。
 *
 * 音は再生時に受け取ったものをブラウザに控えてある。直近の数ターンぶんだけで、
 * ページを閉じると消える（保存した音声を見たい場合は管理画面の会話ログから）。
 */
function ReplayButton({ url }: { url: string }) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);

  useEffect(() => {
    return () => {
      audioRef.current?.pause();
      audioRef.current = null;
    };
  }, []);

  const toggle = () => {
    if (isPlaying) {
      audioRef.current?.pause();
      setIsPlaying(false);
      return;
    }

    const audio = audioRef.current ?? new Audio(url);
    audioRef.current = audio;
    audio.onended = () => setIsPlaying(false);
    audio.onerror = () => setIsPlaying(false);
    audio.currentTime = 0;
    void audio.play().then(
      () => setIsPlaying(true),
      () => setIsPlaying(false),
    );
  };

  return (
    <button
      type="button"
      onClick={toggle}
      className="mt-1 flex w-fit items-center gap-1 rounded px-1 py-0.5 text-[11px] text-slate-400 transition-colors hover:text-[var(--showcan-ink)]"
      aria-label={isPlaying ? "読み上げを停止" : "読み上げをもう一度聞く"}
    >
      {isPlaying ? <Square className="h-3 w-3" /> : <Volume2 className="h-3 w-3" />}
      <span>{isPlaying ? "停止" : "もう一度聞く"}</span>
    </button>
  );
}
